import React from 'react'
import { Form } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $dividendForm,
  addDividend,
  addDividendMounted,
  deleteDividendPayment,
  deleteDividendPaymentMounted,
  editDividendPayment,
  editDividendPaymentMounted,
  getDividendPaymentById,
  getDividendPaymentByIdMounted,
  setDividendForm,
} from './model'
import { $userProfile } from '../../../../../features/user'
import { dividendTransactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $wallet as $currentWallet } from '../../details/model'
import { Currency } from '../../../../../views/widgets/walletCard/types'
import { initialDividendModalState } from './const'
import { ModalType } from '../types'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountSentReceivedRow,
  CommentField,
  CommissionRow,
  DateTimeRow,
  ExchangeRateRow,
  OperationTypeSelect,
  ProductSelect,
  TextField,
  WalletSelect,
  useDefaultCurrencyOnce,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  allCurrencies: Currency[]
  onClose: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  transactionId?: string
  transactionType?: string
  type: ModalType
}

type FormValues = {
  fromWallet: string
  operationType: string
  product: string
  toWallet: string
  sentAmount: string
  sentCurrency: string
  receivedAmount: string
  receivedCurrency: string
  sentCommission: string
  sentCommissionCurrency: string
  receivedCommission: string
  receivedCommissionCurrency: string
  sentExchangeRate: string
  receivedExchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  comment: string
}

export const DividendTransactionModal: React.FC<Props> = ({
  isOpen,
  allCurrencies,
  onClose,
  onCancel,
  transactionId,
  transactionType: initialOperationType,
  mode = 'create',
  type,
}) => {
  const [form] = Form.useForm<FormValues>()

  const userProfile = useStore($userProfile)
  const allWallets = useUnit($walletAllowedList)
  const currentWallet = useStore($currentWallet)

  const fromWallet = Form.useWatch('fromWallet', form)

  useDefaultCurrencyOnce(form, 'sentCurrency', 'sentCommissionCurrency')
  useDefaultCurrencyOnce(form, 'receivedCurrency', 'receivedCommissionCurrency')

  const fromWalletCurrencies = React.useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = allWallets.find((item) => Number(item.id) === Number(fromWallet))
    return w?.currencies ?? []
  }, [allWallets, fromWallet, currentWallet])

  const allCurrencyCodes = React.useMemo(
    () => (allCurrencies ?? []).map((item) => item.code),
    [allCurrencies],
  )

  const {
    storeData: dividendForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $dividendForm,
    initialState: initialDividendModalState,
    setForm: setDividendForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        fromWallet: String(data.fromWallet ?? ''),
        operationType: initialOperationType ?? data.operationType,
        product: data.product,
        toWallet: data.toWallet,
        sentAmount: String(data.sentAmount ?? ''),
        sentCurrency: data.sentCurrency,
        receivedAmount: String(data.receivedAmount ?? ''),
        receivedCurrency: data.receivedCurrency,
        sentCommission: String(data.sentCommission ?? '0'),
        sentCommissionCurrency: data.sentCommissionCurrency,
        receivedCommission: String(data.receivedCommission ?? '0'),
        receivedCommissionCurrency: data.receivedCommissionCurrency,
        sentExchangeRate: String(data.sentExchangeRate ?? '0'),
        receivedExchangeRate: String(data.receivedExchangeRate ?? '0'),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        comment: data.comment,
      }
    },
    events: {
      addMount: addDividendMounted,
      editMount: editDividendPaymentMounted,
      deleteMount: deleteDividendPaymentMounted,
      getByIdMount: getDividendPaymentByIdMounted,
    },
    effects: {
      add: addDividend,
      edit: editDividendPayment,
      delete: deleteDividendPayment,
      getById: getDividendPaymentById,
    },
    buildDeletePayload: (data) => data,
    historyFields: { toWallet: 'finSystem.recipient' },
    buildPayload: (values, { mode: m, transactionId: tid }) => {
      const wallet =
        values.fromWallet ||
        (currentWallet.id ? String(currentWallet.id) : '')
      return {
        ...values,
        id: m === 'edit' ? tid : '',
        fromWallet: wallet,
        walletId: wallet,
        sentCommission: values.sentCommission || '0',
        receivedCommission: values.receivedCommission || '0',
        sentExchangeRate: values.sentExchangeRate || '0',
        receivedExchangeRate: values.receivedExchangeRate || '0',
        dateTime: formatTransactionDateTime(
          values.date!.format('YYYY-MM-DD'),
          values.time!.format('HH:mm:ss'),
        ),
      } as any
    },
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} dividend${dividendForm.id ? ` #${dividendForm.id}` : ''}`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fromWallet:
            type === 'wallet' && currentWallet.id
              ? String(currentWallet.id)
              : '',
          sentCommission: '0',
          receivedCommission: '0',
          sentExchangeRate: '0',
          receivedExchangeRate: '0',
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="fromWallet"
            label="From"
            required
            disabled={type === 'wallet' || mode === 'edit'}
            style={{ flex: 1 }}
          />
          <OperationTypeSelect
            name="operationType"
            required
            disabled={mode === 'edit'}
            options={dividendTransactionType}
            style={{ flex: 1 }}
          />
        </div>

        <ProductSelect name="product" />

        <TextField
          name="toWallet"
          label="To"
          placeholder="Recipient"
          required
          historyScope="finSystem.recipient"
        />

        <AmountSentReceivedRow
          sentAmountName="sentAmount"
          sentCurrencyName="sentCurrency"
          sentCurrencies={fromWalletCurrencies}
          sentAllowNegative
          sentCurrencyAllowClear
          receivedAmountName="receivedAmount"
          receivedCurrencyName="receivedCurrency"
          receivedCurrencies={allCurrencyCodes}
          receivedRequired
          receivedAllowNegative
          receivedCurrencyRequired
        />

        <CommissionRow
          sentName="sentCommission"
          sentCurrencyName="sentCommissionCurrency"
          sentCurrencies={allCurrencyCodes}
          receivedName="receivedCommission"
          receivedCurrencyName="receivedCommissionCurrency"
          receivedCurrencies={allCurrencyCodes}
        />

        <ExchangeRateRow
          sentName="sentExchangeRate"
          receivedName="receivedExchangeRate"
        />

        <DateTimeRow />

        <CommentField />
      </Form>
    </TransactionModalLayout>
  )
}
